import { SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { produce } from "immer";

import MetricCard from "../../Tabs/PerformanceMetrics/MetricCard/MetricCard";
import { parseNoteContent } from "../../Tabs/Notes/notesConfig";
import { addImageToSeries, getFlagImage, getSvg } from "../util/addImageToSeries";
import {
  DownlineProfileDocumentProps,
  InternationalSponsorWithFlagImage,
  KpiSeries,
  PdfFetchProfileImages,
} from "../../shared/types";
import { i18n } from "../../../../i18n.config";

import {
  useLazyGetSeriesDataviewQuery,
  useLazyGetABOProfileQuery,
  useLazyGetProfileNotesQuery,
  useGetABOProfileQuery,
  useGetSeriesDataviewQuery,
  useGetProfileNotesQuery,
  useGetAboLineageQuery,
  useLazyGetAboLineageQuery,
} from "../../../../services/AboProfile/AboProfileAPI";
import {
  AboLineageResponse,
  MetricsResponse,
  NotesResponse,
} from "../../../../services/AboProfile/AboProfileAPI.types";
import { ProfileTransformResponse } from "../../../../services/Profile/profileAPI.types";

import { isRootLineage } from "../../../../common/components/DownlineProfileLOS/DownlineProfileIcon";
import { getCountryFlagByAff } from "../../../../common/util/getCountryFlag";
import { isIOS } from "../../../../common/util/browserSupport";
import { IconDownloadButton } from "../../../../common/icons/IconDownloadButton/IconDownloadButton";
import IconLoadingCircle from "../../../../common/icons/IconLoadingCircle/IconLoadingCircle";

import { IconNoteCreate } from "../../../../common/icons/IconNoteCreate/IconNoteCreate";
import { useLazyGetWatchlistQuery } from "../../../../services/ActionReports/WatchlistBff";

import { marketCode, storedLocale } from "../../../Authentication/util";
import { useAppSelector } from "../../../../store";
import defaultContent from "../../../../content/downlineProfile";
import "./ExportButton.scss";
import type { WatchlistResponse } from "../../../../services/ActionReports/ActionReportAPI.types";
import { PROFILE_TABS } from "../../../../reducers/downlineProfile";
import { getSopStatusForAbo } from "../../Tabs/SopStatus/sopStatusData";
import {
  trackCloseExportModal,
  trackDownloadExport,
  trackExportModalOpen,
} from "../../PlatinumProfile/downlineProfileTealium";
import useGetProfileImage from "../../hooks/useGetProfileImage";

if (typeof window !== "undefined") {
  const windowWithBuffer = window as Window & {
    Buffer?: {
      from: (data: ArrayBuffer | Uint8Array | number[]) => Uint8Array;
      isBuffer: (obj: unknown) => boolean;
    };
  };
  if (!windowWithBuffer.Buffer) {
    windowWithBuffer.Buffer = {
      from: (data) => (ArrayBuffer.isView(data) || Array.isArray(data) ? Uint8Array.from(data) : new Uint8Array(data)),
      isBuffer: (obj: unknown) => obj instanceof Uint8Array,
    };
  }
}

const PDF_TRANSLATION_NAMESPACES = ["downlineProfile", "common", "awardLabels"] as const;

const getI18nResources = (locale: string): Record<string, unknown> => {
  const resources: Record<string, unknown> = {};
  PDF_TRANSLATION_NAMESPACES.forEach((ns) => {
    resources[ns] = i18n.getResourceBundle(locale, ns) ?? {};
  });
  return resources;
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const handleWorker = (
  worker: Worker,
  transformedDataWithImage: DownlineProfileDocumentProps,
  signal: AbortSignal,
): Promise<undefined> => {
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      reject(new DOMException("PDF export cancelled", "AbortError"));
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener("abort", onAbort);
    worker.onmessage = async (e) => {
      signal.removeEventListener("abort", onAbort);
      if (signal.aborted) return;
      if (e.data.success) {
        const filename = `${transformedDataWithImage.profileBanner.aboNumber}-profile-export.pdf`;

        // iOS Safari doesn't reliably honor a script-triggered `<a download>` click on a
        // blob URL. The Web Share API hands the PDF to the native share sheet instead
        // (Save to Files, AirDrop, etc.) -- scoped to iOS only, since desktop/Android's
        // plain anchor-download already works fine and shouldn't be replaced with an
        // OS share-sheet popup just because the browser happens to support file sharing.
        // Only `files` is passed -- adding `title` (or `text`) makes iOS treat it as a
        // second shared item, which "Save to Files" saves as its own stray text file.
        // The shared filename gets a MMDD-HHmm suffix: unlike the browser's own download
        // dialog (used below), iOS's Save to Files doesn't dedupe a repeat name on its own.
        const now = new Date();
        const pad = (n: number) => `${n}`.padStart(2, "0");
        const timestamp = `${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
        const sharedFilename = filename.replace(/\.pdf$/, `-${timestamp}.pdf`);
        const file = new File([e.data.blob], sharedFilename, { type: "application/pdf" });

        if (isIOS && navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({ files: [file] });
            resolve(undefined);
            return;
          } catch (error) {
            if ((error as Error)?.name === "AbortError") {
              // User dismissed the native share sheet on purpose -- don't fall through
              // to a download they didn't ask for.
              resolve(undefined);
              return;
            }
          }
        }

        downloadBlob(e.data.blob, filename);
        resolve(undefined);
      } else {
        reject(new Error(e.data.error ?? "PDF generation failed"));
      }
    };
    worker.onerror = (e) => {
      signal.removeEventListener("abort", onAbort);
      reject(e.error ?? new Error(e.message || "PDF worker error"));
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured only to omit tFunction, which can't cross postMessage
    const { tFunction, ...documentProps } = transformedDataWithImage;
    const locale = transformedDataWithImage.performanceMetrics.locale ?? "en-US";
    const market = marketCode();
    worker.postMessage({
      documentProps,
      locale,
      market,
      i18nResources: getI18nResources(locale),
    });
  });
};

type ExportPdf = (documentProps: DownlineProfileDocumentProps, signal: AbortSignal) => Promise<void>;
const exportPdf: ExportPdf = async (transformedData, signal) => {
  const shouldFetchFlagImage = transformedData.keyInfo.aboInfo?.internationalSponsor.isValid ?? false;
  const shouldFetchNoNotesImage = transformedData.notes.content === null;

  const [imagedSeries, flagImage, noNotesImage] = await Promise.all([
    addImageToSeries(transformedData.performanceMetrics.series),
    shouldFetchFlagImage ? getFlagImage("international-flag-searchId") : Promise.resolve(null),
    shouldFetchNoNotesImage ? getSvg("no-notes-searchId", "", 4) : Promise.resolve(null),
  ]);

  if (signal.aborted) {
    return;
  }

  const transformedDataWithImage = produce(transformedData, (draft) => {
    const performanceMetrics = draft.performanceMetrics;
    const internationalSponsor = draft.keyInfo.aboInfo?.internationalSponsor;
    const notes = draft.notes;
    if (imagedSeries) {
      performanceMetrics.series = imagedSeries;
    }
    if (flagImage && internationalSponsor && internationalSponsor.isValid) {
      internationalSponsor.flagImage = flagImage;
    }
    if (noNotesImage) {
      notes.notesImage = noNotesImage;
    }
  });

  const worker = new Worker(new URL("./pdf.worker.tsx", import.meta.url), { type: "module" });
  try {
    await handleWorker(worker, transformedDataWithImage, signal);
  } finally {
    worker.terminate();
  }
};

type ProfileConfig = DownlineProfileDocumentProps["keyInfo"]["profileConfig"];
type PrivacyConfig = DownlineProfileDocumentProps["privacyConfig"];

export const transformPDFData = (
  profileInfo: ProfileTransformResponse | undefined,
  metricsInfo: MetricsResponse | undefined,
  notesData: NotesResponse | undefined,
  watchlistResult: WatchlistResponse | undefined,
  lineageInfo: AboLineageResponse | undefined,
  profileConfig: ProfileConfig,
  hideWhatsApp: boolean,
  privacyConfig: PrivacyConfig,
  aboConfig: { aff: string; abo: string; address?: string; addressLineDivider?: string; dateFormat?: "long" | "short" },
  t: TFunction<readonly ["downlineProfile", "common", "awardLabels"]>,
  imageData: PdfFetchProfileImages | undefined = undefined,
  sopStatus: DownlineProfileDocumentProps["sopStatus"] = undefined,
): DownlineProfileDocumentProps => {
  const {
    aboInfo,
    pinAwardSummary,
    partyInfo,
    additionalRegInfo,
    orderInfo,
    isPlatinumOrAbove = false,
    isEmeraldAndAbove = false,
    isDiamondAndAbove = false,
  } = profileInfo ?? {};
  const primaryParty = partyInfo?.find((p) => p.isPrimary) ?? partyInfo?.[0];
  const { displayNumber: phoneNumber } = primaryParty?.phoneNumbers?.[0] ?? {};
  const { email } = primaryParty?.emailAddresses?.[0] ?? {};
  const { ecommAddr } = primaryParty?.whatsApp?.[0] ?? {};

  const portalLocale = storedLocale().split("-")[0];
  const whatsAppContactNumber = ecommAddr?.replace(/\D/g, "");
  const selectedLineageEntry = (lineageInfo?.lineage ?? []).at(-1);
  const note = notesData?.notes?.[0] ?? null;
  const noteContent = parseNoteContent(note?.content);
  const hasNotes = noteContent !== null;

  const isInWatchlist =
    watchlistResult?.watchList?.some((item) => item.ABONo === aboConfig.abo && +item.Aff === +aboConfig.aff) ?? false;
  const includedSopStatus = sopStatus?.subscriptions.length ? sopStatus : undefined;
  return {
    profileBanner: {
      displayName: aboInfo?.displayName ?? "",
      aboNumber: aboInfo?.aboNo ?? "",
      award: {
        highestAward: pinAwardSummary?.previousPYPin?.code,
        highestAwardDate: pinAwardSummary?.previousPYPin?.qualPeriod,
        isPlatinumOrAbove,
        isEmeraldAndAbove,
        isDiamondAndAbove,
      },
      lineageNumber: selectedLineageEntry?.losNumber,
      isRootAbo: isRootLineage(lineageInfo?.lineage),
      tags: {
        isOnSop: false,
        isOnAutoRenewal: profileConfig?.showAutoRenewal && additionalRegInfo?.autoRenewal,
        hasNotes: hasNotes,
      },
      phone: phoneNumber,
      email: email,
      whatsAppContactNumber: whatsAppContactNumber,
      hideWhatsApp: hideWhatsApp,
      isInWatchlist,
      imageRefUrl:
        imageData?.profileImages.find(
          (e) =>
            String(e.aff).padStart(3, "0") === aboInfo?.affNo?.padStart(3, "0") &&
            String(e.abo) === String(aboInfo.aboNo),
        )?.profileImage ?? "",
      privacyFlag: aboInfo?.privacyFlag,
    },
    keyInfo: {
      aboInfo: aboInfo,
      orderInfo: orderInfo,
      pinAwardSummary: pinAwardSummary,
      additionalRegInfo: additionalRegInfo,
      partyInfo: partyInfo,
      isPlatinumOrAbove,
      isEmeraldAndAbove,
      isDiamondAndAbove,
      profileConfig,
      imageData,
    },
    performanceMetrics: {
      series: metricsInfo?.series,
      selectedKpis: metricsInfo?.selectedKpis,
      locale: storedLocale(),
      endPeriod: metricsInfo?.endPeriod,
    },
    notes: {
      lastEdited: note?.lastModified ?? null,
      content: noteContent,
    },
    privacyConfig: { ...privacyConfig, portalLocale: portalLocale, privacyFlag: aboInfo?.privacyFlag },
    formatConfig: {
      addressFormat: aboConfig.address,
      addressLineDivider: aboConfig.addressLineDivider,
      dateFormat: aboConfig.dateFormat,
    },
    ...(includedSopStatus ? { sopStatus: includedSopStatus } : {}),
    tFunction: t,
  };
};

interface QuestionComponentProps {
  setShowExport: React.Dispatch<SetStateAction<boolean>>;
  setPortaledData: React.Dispatch<
    SetStateAction<{
      series: { series?: KpiSeries[] | null; endPeriod?: number };
      internationalSponsor?: InternationalSponsorWithFlagImage;
      hasNotes: boolean;
    } | null>
  >;
  queryDocumentProps: () => Promise<{
    isError: boolean;
    transformedData: DownlineProfileDocumentProps;
  }>;
  t: TFunction<readonly ["downlineProfile", "common", "awardLabels"], undefined>;
  rootAbo?: boolean;
}
const QuestionComponent = ({
  setShowExport,
  setPortaledData,
  queryDocumentProps,
  t,
  rootAbo = false,
}: QuestionComponentProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  return (
    <div className="question-component-wrapper">
      <div className="question-component-wrapper__content">
        <p className="question-component-wrapper__content__title">
          {rootAbo ? (
            <> {t("pdfExport.exportProfile", defaultContent.pdfExport.exportProfile, { ns: "downlineProfile" })}</>
          ) : (
            <> {t("pdfExport.exportText", defaultContent.pdfExport.exportText, { ns: "downlineProfile" })}</>
          )}
        </p>
        <p className="question-component-wrapper__content__desc">
          {rootAbo ? (
            <> {t("pdfExport.downloadProfile", defaultContent.pdfExport.downloadProfile, { ns: "downlineProfile" })}</>
          ) : (
            <> {t("pdfExport.downloadText", defaultContent.pdfExport.downloadText, { ns: "downlineProfile" })}</>
          )}
        </p>
        <div className="question-component-wrapper__content__buttons">
          <button
            onClick={() => {
              abortControllerRef.current?.abort();
              setShowExport(false);
              trackCloseExportModal();
            }}
          >
            {t("pdfExport.cancel", defaultContent.pdfExport.cancel, { ns: "downlineProfile" })}
          </button>
          <button
            disabled={isLoading}
            onClick={async () => {
              trackDownloadExport();
              setIsLoading(true);
              const abortController = new AbortController();
              abortControllerRef.current = abortController;
              try {
                const { isError, transformedData } = await queryDocumentProps();
                setPortaledData({
                  series: {
                    series: transformedData.performanceMetrics.series,
                    endPeriod: transformedData.performanceMetrics.endPeriod,
                  },
                  internationalSponsor: transformedData?.keyInfo?.aboInfo?.internationalSponsor,
                  hasNotes: transformedData.profileBanner.tags?.hasNotes ?? false,
                });
                if (transformedData && !isError) {
                  await exportPdf(transformedData, abortController.signal);
                }
                if (!abortController.signal.aborted) {
                  setTimeout(() => setShowExport(false), 0);
                }
              } catch (err) {
                if ((err as Error)?.name !== "AbortError") {
                  throw err;
                }
              } finally {
                abortControllerRef.current = null;
                setIsLoading(false);
              }
            }}
          >
            {isLoading ? (
              <IconLoadingCircle />
            ) : (
              <>{`${t("pdfExport.export", defaultContent.pdfExport.export, { ns: "downlineProfile" })}`}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExportButton = () => {
  const { t } = useTranslation(["downlineProfile", "common", "awardLabels"]);
  const currentAffAbo = useAppSelector((s) => s.downlineProfile.affAbo);
  const hideWhatsApp = useAppSelector((state) => state.boot.configuration.hideWhatsApp);
  const [aff = "", abo = ""] = (currentAffAbo ?? "").split("-");

  const profile = useAppSelector((state) => state.boot.configuration.profile);
  const {
    showAutoRenewal,
    showContractSigned,
    showOrderBlock,
    showSponsorBlock,
    showRenewalConsent: renewalConsentFlag,
  } = profile?.additionalRegInfo ?? {};
  const showSopStatus = profile?.aboProfileTabs.includes(PROFILE_TABS.SOP_STATUS) ?? false;
  const showRenewalDate = useAppSelector((state) => state.boot.configuration.profile?.aboInfo?.showRenewalDate ?? true);
  const {
    aboName: nameFormat,
    address,
    addressLinesDivider: addressLineDivider,
    date: dateFormat,
  } = useAppSelector((state) => state?.boot?.configuration?.profile?.format) ?? {};
  const activeTab = useAppSelector((s) => s.downlineProfile.activeTab);
  const isApplyingKpis = useAppSelector((s) => s.downlineProfile.isApplyingKpis);
  const { user } = useAppSelector((s) => s.boot);
  const isFromEdLos = useAppSelector((s) => s.profile.isFromEdLos);
  const edlosRootAffAbo = useAppSelector((s) => s.profile.edlosRootAffAbo);
  const edlosRootDepth = useAppSelector((s) => s.profile.edlosRootDepth);
  const rootAbo = currentAffAbo === `${user.aff}-${user.abo}`;
  const [triggerMetrics] = useLazyGetSeriesDataviewQuery();
  const [triggerProfile] = useLazyGetABOProfileQuery();
  const [triggerNotes] = useLazyGetProfileNotesQuery();
  const [triggerWatchlist] = useLazyGetWatchlistQuery();
  const [triggerLineage] = useLazyGetAboLineageQuery();
  const lineageQueryArgs = useMemo(
    () => ({
      aff,
      abo,
      IsEdlos: isFromEdLos,
      ...(isFromEdLos && edlosRootAffAbo ? { EdlosRootAffAbo: edlosRootAffAbo } : {}),
      ...(isFromEdLos && edlosRootDepth !== null ? { EdlosRootDepth: edlosRootDepth } : {}),
    }),
    [aff, abo, isFromEdLos, edlosRootAffAbo, edlosRootDepth],
  );
  const profileQueryArgs = useMemo(
    () => ({ aff, abo, ...(isFromEdLos ? { includePlatinumAndAbove: true } : {}) }),
    [aff, abo, isFromEdLos],
  );
  const { isFetching: profileLoading, isError: profileError } = useGetABOProfileQuery(profileQueryArgs, {
    skip: !aff || !abo,
  });
  const { isFetching: metricsLoading, isError: metricsError } = useGetSeriesDataviewQuery(
    { aff, abo, locale: storedLocale(), dataViewID: "downlineProfile", market: marketCode() },
    { skip: !aff || !abo },
  );
  const { isFetching: notesLoading, isError: notesError } = useGetProfileNotesQuery(
    { affabo: currentAffAbo },
    { skip: activeTab !== "notes" || !currentAffAbo },
  );
  const { isFetching: lineageLoading, isError: lineageError } = useGetAboLineageQuery(lineageQueryArgs, {
    skip: !aff || !abo,
  });
  const { pdfFetchProfileImages } = useGetProfileImage(undefined, true, undefined);
  const isQueryLoading = profileLoading || metricsLoading || notesLoading || lineageLoading || isApplyingKpis;
  const isQueryError = profileError || metricsError || notesError || lineageError;
  const queryDocumentProps = useCallback(async () => {
    const [metricsResult, profileResult, notesResult, watchlistResult, lineageResult] = await Promise.all([
      triggerMetrics({ aff, abo, locale: storedLocale(), dataViewID: "downlineProfile", market: marketCode() }, true),
      triggerProfile(profileQueryArgs, true),
      triggerNotes({ affabo: currentAffAbo }, true),
      triggerWatchlist({ ownerAff: user.aff, ownerAbo: user.abo }),
      triggerLineage(lineageQueryArgs, true),
    ]);
    const metricsInfo = metricsResult.data;
    const profileInfo = profileResult.data;
    const notesData = notesResult.data;
    const watchlistData = watchlistResult.data;
    const lineageData = lineageResult.data;
    const isError =
      !!metricsResult.error ||
      !!profileResult.error ||
      !!notesResult.error ||
      !!watchlistResult.error ||
      !!lineageResult.error;
    const showRenewalConsent = renewalConsentFlag && profileInfo?.additionalRegInfo?.renewalConsent !== undefined;
    const imageData = await pdfFetchProfileImages(profileInfo);
    const transformedData = await transformPDFData(
      profileInfo,
      metricsInfo,
      notesData,
      watchlistData,
      lineageData,
      {
        showAutoRenewal,
        showRenewalDate,
        showContractSigned,
        showOrderBlock,
        showSponsorBlock,
        showRenewalConsent,
      },
      hideWhatsApp,
      { nameFormat },
      { abo, aff, address, addressLineDivider, dateFormat },
      t,
      imageData,
      showSopStatus ? getSopStatusForAbo(currentAffAbo) : undefined,
    );

    return { isError, transformedData };
  }, [
    showRenewalDate,
    address,
    addressLineDivider,
    showAutoRenewal,
    showContractSigned,
    showSopStatus,
    showOrderBlock,
    showSponsorBlock,
    hideWhatsApp,
    aff,
    abo,
    nameFormat,
    dateFormat,
    currentAffAbo,
    user.abo,
    user.aff,
    t,
    triggerMetrics,
    triggerNotes,
    triggerProfile,
    triggerWatchlist,
    triggerLineage,
    lineageQueryArgs,
    profileQueryArgs,
    pdfFetchProfileImages,
  ]);
  const [showExport, setShowExport] = useState(false);
  const hiddenContainer = document.querySelector<HTMLDivElement>(".downline-profile-modal__hidden");
  const [portaledData, setPortaledData] = useState<{
    series: { series?: KpiSeries[] | null; endPeriod?: number };
    internationalSponsor?: InternationalSponsorWithFlagImage;
    hasNotes: boolean;
  } | null>(null);
  const modal = document.querySelector(".downline-profile-modal");

  return (
    <>
      <button
        type="button"
        className="downline-profile-export-btn"
        data-testid="downline-profile-export-btn"
        aria-label={t("toolbar.export", defaultContent.toolbar.export, { ns: "downlineProfile" }) as string}
        onClick={() => {
          setShowExport((prev) => !prev);
          trackExportModalOpen();
        }}
        disabled={isQueryLoading || isQueryError}
      >
        <IconDownloadButton width="1.125rem" height="1.125rem" />{" "}
        <p>{t("toolbar.export", defaultContent.toolbar.export, { ns: "downlineProfile" }) as string}</p>
      </button>
      {showExport &&
        modal &&
        createPortal(
          <QuestionComponent
            setShowExport={setShowExport}
            queryDocumentProps={queryDocumentProps}
            setPortaledData={setPortaledData}
            rootAbo={rootAbo}
            t={t}
          />,
          modal,
        )}
      {/* Render elements in hidden container to capture svg */}
      {portaledData?.series.series &&
        hiddenContainer &&
        createPortal(
          (portaledData?.series.series ?? [null, null, null, null]).map((s, i) => (
            <MetricCard
              key={s?.kpi ?? i}
              series={s}
              endPeriod={portaledData?.series.endPeriod}
              isLoading={false}
              isError={false}
              searchId={`${s?.kpi}-searchId`}
            />
          )),
          hiddenContainer,
        )}
      {portaledData?.internationalSponsor?.isValid &&
        hiddenContainer &&
        createPortal(
          <span id="international-flag-searchId">
            {getCountryFlagByAff(`${portaledData?.internationalSponsor.affNo}`)}
          </span>,
          hiddenContainer,
        )}
      {!portaledData?.hasNotes &&
        hiddenContainer &&
        createPortal(
          <span className="no-notes-searchId">
            <IconNoteCreate />
          </span>,
          hiddenContainer,
        )}
    </>
  );
};

export default ExportButton;
